import {useAccessToken, useIsUnitUser} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findOrgs, Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import Loading from "../../components/Loading/Loading"
import React, {useState, useEffect, useMemo} from "react"
import {MainSection} from "../../containers/MainSection/MainSection"
import {BillpayTerms, getBillpayTerms, updateBillpayTerms, PaymentMethodTerms} from "../../resources/billpayTerms"
import {useParams} from "react-router"
import TitleBar from "../../components/TitleBar/TitleBar"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import Card from "../../containers/Card/Card"
import Icon from "../../components/Icon/Icon"
import {useToasts} from "react-toast-notifications"
import {v4 as uuidv4} from "uuid"
import TieredFeeEditor from "./TieredFeeEditor/TieredFeeEditor"
import {validatePaymentMethodFees} from "./billPayTermsValidation"
import {HorizontalField} from "./HorizontalField"

function Page({billpayTerms, orgs, refresh}: {billpayTerms: BillpayTerms; orgs: Array<Org>; refresh: () => void}) {
	const orgId = billpayTerms.relationships?.org?.data.id
	const isUnitUser = useIsUnitUser()
	const [isEditing, setIsEditing] = useState(false)
	const {addToast} = useToasts()
	const accessToken = useAccessToken()
	const [editedName, setEditedName] = useState<string>(billpayTerms.attributes.name)
	const [editedAch, setEditedAch] = useState<PaymentMethodTerms>(billpayTerms.attributes.ach)
	const [editedCheck, setEditedCheck] = useState<PaymentMethodTerms>(billpayTerms.attributes.check)
	const [editedWire, setEditedWire] = useState<PaymentMethodTerms>(billpayTerms.attributes.wire)
	const [editedSameDayAch, setEditedSameDayAch] = useState<PaymentMethodTerms>(billpayTerms.attributes.sameDayAch)
	const [editedExternalFunding, setEditedExternalFunding] = useState(billpayTerms.attributes.externalFunding)
	const [isSaving, setIsSaving] = useState(false)

	// Reset edited values when billpayTerms changes or when exiting edit mode
	useEffect(() => {
		if (!isEditing) {
			setEditedName(billpayTerms.attributes.name)
			setEditedAch(billpayTerms.attributes.ach)
			setEditedCheck(billpayTerms.attributes.check)
			setEditedWire(billpayTerms.attributes.wire)
			setEditedSameDayAch(billpayTerms.attributes.sameDayAch)
			setEditedExternalFunding(billpayTerms.attributes.externalFunding)
		}
	}, [billpayTerms, isEditing])

	const handleSave = async () => {
		// Validate that fees are provided when payment methods are allowed
		if (
			!validatePaymentMethodFees(
				[
					{label: "ACH", terms: editedAch},
					{label: "Check", terms: editedCheck},
					{label: "Wire", terms: editedWire},
					{label: "Same Day ACH", terms: editedSameDayAch},
				],
				addToast
			)
		) {
			return
		}

		setIsSaving(true)
		const result = await updateBillpayTerms(
			accessToken,
			billpayTerms.id,
			editedName !== billpayTerms.attributes.name ? editedName : undefined,
			editedExternalFunding,
			editedAch,
			editedCheck,
			editedWire,
			editedSameDayAch,
			uuidv4()
		)

		result.match(
			() => {
				addToast("Bill Pay Terms updated successfully", {appearance: "success"})
				setIsEditing(false)
				refresh()
				setIsSaving(false)
			},
			(error) => {
				if (error.errors && error.errors.length > 0) {
					addToast(error.errors[0].detail || error.errors[0].title, {appearance: "error"})
				} else {
					addToast("Failed to update Bill Pay Terms", {appearance: "error"})
				}
				setIsSaving(false)
			}
		)
	}

	const handleDiscard = () => {
		setIsEditing(false)
		refresh()
	}

	const orgName = useMemo(() => {
		return orgId ? orgs.find((org) => org.id === orgId)?.attributes.name ?? "" : ""
	}, [orgId, orgs])

	return (
		<>
			<TitleBar
				title={billpayTerms.attributes.name}
				breadcrumbs={[{text: "Bill Pay Terms", path: "/admin?tab=Bill%20Pay%20Terms"}]}
				meta={
					orgId
						? [
								{
									text: orgName,
									icon: "user-hierachy2",
								},
						  ]
						: undefined
				}
			>
				{isUnitUser && (
					<>
						{!isEditing ? (
							<button className="button page-title-button" onClick={() => setIsEditing(true)}>
								<Icon icon={"pencil-1--interface-essential"} size={16} />
								<span>{"Edit"}</span>
							</button>
						) : (
							<>
								<button className="button button-cancel" onClick={handleDiscard} disabled={isSaving}>
									<Icon icon={"interface-delete-interface-essential"} size={12} />
									<span>{"Discard"}</span>
								</button>
								<button className="button page-title-button" onClick={handleSave} disabled={isSaving}>
									<span>{isSaving ? "Saving..." : "Save"}</span>
								</button>
							</>
						)}
					</>
				)}
			</TitleBar>

			<MainSection>
				<div className="billpay-terms-detail">
					<div className="billpay-terms-form">
						<Card title="General">
							<div className="card-content">
								<HorizontalField label="Name">
									<input
										className="input"
										type="text"
										value={isEditing ? editedName : billpayTerms.attributes.name}
										disabled={!isEditing}
										readOnly={!isEditing}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditedName(e.target.value)}
									/>
								</HorizontalField>
								<HorizontalField label="Org">
									<input className="input" type="text" value={orgName || "--"} disabled readOnly />
								</HorizontalField>
								<HorizontalField label="Status">
									<input className="input" type="text" value={billpayTerms.attributes.status} disabled readOnly />
								</HorizontalField>
								<HorizontalField label="External Funding">
									<label className="checkbox">
										<input
											type="checkbox"
											checked={isEditing ? editedExternalFunding : billpayTerms.attributes.externalFunding}
											disabled={!isEditing}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditedExternalFunding(e.target.checked)}
										/>{" "}
										Enable
									</label>
								</HorizontalField>
							</div>
						</Card>

						<Card title="Transaction Fees">
							<div className="card-content transaction-fees-content">
								<TieredFeeEditor
									label="ACH"
									value={isEditing ? editedAch : billpayTerms.attributes.ach}
									onChange={setEditedAch}
									isEditing={isEditing}
								/>
								<TieredFeeEditor
									label="Same Day ACH"
									value={isEditing ? editedSameDayAch : billpayTerms.attributes.sameDayAch}
									onChange={setEditedSameDayAch}
									isEditing={isEditing}
								/>
								<TieredFeeEditor
									label="Wire"
									value={isEditing ? editedWire : billpayTerms.attributes.wire}
									onChange={setEditedWire}
									isEditing={isEditing}
								/>
								<TieredFeeEditor
									label="Check"
									value={isEditing ? editedCheck : billpayTerms.attributes.check}
									onChange={setEditedCheck}
									isEditing={isEditing}
								/>
							</div>
						</Card>
					</div>
				</div>
			</MainSection>
		</>
	)
}

function BillPayTermsThatCanView() {
	const {billpayTermsId = ""} = useParams<{billpayTermsId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()

	const billpayTerms = useAsyncResult(() => getBillpayTerms(accessToken, billpayTermsId), [refreshToken])
	const orgs = useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])

	return AsyncResult.zip(billpayTerms, orgs).match(
		() => <Loading />,
		([billpayTerms, orgs]) => <Page billpayTerms={billpayTerms} orgs={orgs} refresh={refresh} />,
		(_) => null
	)
}

export default function BillPayTermsPage() {
	return <BillPayTermsThatCanView />
}
